import React, { useEffect, useMemo, useRef, useState } from 'react'
import Spacer from '@berlitz/spacer'
import { FormattedMessage } from 'react-intl'
import Switch from '@components/Switch'
import { SPSwitch, PrefDesc, SPWrapper } from './style'
import Radio from '@components/Radio'
import { useApolloClient } from 'react-apollo'
import { PUT_PROGRAMS } from '@mutations/programs'
import PortalStudentProfile from '@classes/StudentProfile'
import { StoreProps } from '@interfaces/StoreState'
import { useSelector } from 'react-redux'
import useRefetchProfile from '@hooks/useRefetchProfile'
import { uniqueId } from 'lodash'
import ErrorContent from '@components/Modal/GenericErrorModal'
import Modal from '@berlitzplatforms/micro.ui.modal'

interface SessionPreferencesProps {
  programId?: string
}

const SessionPreferences: React.FC<SessionPreferencesProps> = ({ programId }) => {
  const compId = useMemo(() => uniqueId('cmp-sp'), [])
  const loaded = useRef<boolean>(false) // ref used to ensure the state change came from user interaction
  const client = useApolloClient()
  const refetchProfile = useRefetchProfile()
  const { studentProfile } = useSelector(({ userProfile }: StoreProps) => ({
    studentProfile: new PortalStudentProfile(userProfile.info?.StudentProfile?.StudentProfileInfo),
  }))
  const action = useMemo(
    () => studentProfile.Programs?.[programId!]?.program?.InstructorUnavailabilityAction,
    [studentProfile]
  )
  const [selected, setSelected] = useState<string | undefined>()
  const [toggle, setToggle] = useState<boolean>()
  const [updating, setUpdating] = useState(false)
  const [hasError, setHasError] = useState(false)
  const isReadOnly = useMemo(() => !studentProfile.Programs?.[programId!]?.isStudentAbleToBook, [programId])

  useEffect(() => {
    const process = async () => {
      if (!loaded.current || toggle === undefined || (toggle && !selected) || isReadOnly) {
        return
      }
      // reset ref to ensure that api is not called more than once
      loaded.current = false
      setUpdating(true)
      try {
        const res = await client.mutate({
          mutation: PUT_PROGRAMS,
          variables: {
            programInput: {
              ProgramStudentInfo: {
                ProgramId: programId,
                InstructorUnavailabilityAction: selected,
              },
            },
          },
        })
        if (!res.data.putPrograms?.Success) {
          setHasError(true)
        }
        const [updateUI] = await refetchProfile()
        await updateUI()
      } catch (e) {
        setHasError(true)
        console.error(e)
      } finally {
        setUpdating(false)
      }
    }

    process()
  }, [selected, toggle])

  useEffect(() => {
    setSelected(action)
    setToggle(!!action)
  }, [action])

  return (
    <SPWrapper updating={updating}>
      <Spacer size="md" />
      <PrefDesc>
        <FormattedMessage
          id="If your instructor becomes unavailable for your booked session(s)"
          defaultMessage="If your instructor becomes unavailable for your booked session(s)"
        />
      </PrefDesc>
      <Spacer size="md" />
      <Spacer size="xxxs" />
      <SPSwitch>
        <Switch
          name={`save-pref-${compId}`}
          size="sm"
          disabled={updating || isReadOnly}
          onChange={() => {
            setToggle(!toggle)
            setSelected(undefined)
            loaded.current = true
          }}
          checked={toggle}
        />
        <FormattedMessage
          id="Save preference for future bookings"
          defaultMessage="Save preference for future bookings"
        />
      </SPSwitch>
      <Spacer size="xs" />
      <Radio.Group
        name="session-prefs"
        value={selected}
        onChange={(e) => {
          if (updating) {
            return
          }
          setSelected(e.target.value)
          setToggle(true)
          loaded.current = true
        }}
      >
        <Radio.Button disabled={isReadOnly} id={`cancel-${compId}`} value="Cancel" checked={selected === 'Cancel'}>
          <FormattedMessage id="Cancel the session/s" defaultMessage="Cancel the session/s" />
        </Radio.Button>
        <Radio.Button disabled={isReadOnly} id={`sub-${compId}`} value="Substitute" checked={selected === 'Substitute'}>
          <FormattedMessage id="Assign a new instructor" defaultMessage="Assign a new instructor" />
        </Radio.Button>
      </Radio.Group>
      <Spacer size="xxxs" />
      <Modal open={hasError} onClose={() => window.location.reload()}>
        <ErrorContent
          hideSupportBtn={true}
          onClose={() => window.location.reload()}
          title={'An unknown error has occurred'}
          description={'Please try reloading the page. If the problem persists please raise a support request.'}
        />
      </Modal>
    </SPWrapper>
  )
}

export default SessionPreferences
